import type {
  LeadBuyerType,
  LeadContactMethod,
  LeadFrequency,
  LeadProduct,
  LeadQuantityRange,
  LeadRequest,
  LeadRequestDraft,
  LeadRequestValidation,
} from '../domain/leadRequest.types'

export const leadBuyerTypes: readonly Readonly<{ value: LeadBuyerType; label: string }>[] = [
  { value: 'business', label: 'Empresa' },
  { value: 'personal', label: 'Compra personal' },
]

export const leadProducts: readonly Readonly<{ value: LeadProduct; label: string; note: string }>[] = [
  { value: 'pistachio', label: 'Pistache', note: 'Desde compra personal hasta gran volumen.' },
  { value: 'almond', label: 'Almendra', note: 'Menudeo, negocio y suministro.' },
  { value: 'matcha', label: 'Matcha japonés', note: 'Para bebidas, repostería y operación comercial.' },
  { value: 'cacao-coffee', label: 'Cacao y café', note: 'Disponibilidad por categoría y origen.' },
  { value: 'other', label: 'Otro producto', note: 'Cuéntanos qué estás buscando.' },
]

export const leadQuantityRanges: readonly Readonly<{ value: LeadQuantityRange; label: string; note: string }>[] = [
  { value: '1-10', label: '1–10 kg', note: 'Compra personal o prueba.' },
  { value: '11-100', label: '11–100 kg', note: 'Negocio y consumo frecuente.' },
  { value: '101-500', label: '101–500 kg', note: 'Volumen comercial.' },
  { value: '501-999', label: '501–999 kg', note: 'Mayoreo.' },
  { value: '1000-plus', label: '1 tonelada o más', note: 'Gran volumen.' },
  { value: 'unknown', label: 'Aún no lo sé', note: 'Podemos ayudarte a definirlo.' },
]

export const leadFrequencies: readonly Readonly<{ value: LeadFrequency; label: string }>[] = [
  { value: 'one-time', label: 'Compra puntual' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quincenal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'recurrent-flexible', label: 'Frecuencia por definir' },
  { value: 'evaluating', label: 'Aún lo estoy evaluando' },
]

export const leadContactMethods: readonly Readonly<{ value: LeadContactMethod; label: string }>[] = [
  { value: 'email', label: 'Correo' },
  { value: 'whatsapp', label: 'WhatsApp' },
]

const allowedBuyerTypes = new Set<LeadBuyerType>(leadBuyerTypes.map((item) => item.value))
const allowedProducts = new Set<LeadProduct>(leadProducts.map((item) => item.value))
const allowedQuantityRanges = new Set<LeadQuantityRange>(leadQuantityRanges.map((item) => item.value))
const allowedFrequencies = new Set<LeadFrequency>(leadFrequencies.map((item) => item.value))
const allowedContactMethods = new Set<LeadContactMethod>(leadContactMethods.map((item) => item.value))

export const leadRequestPolicy = Object.freeze({
  maxContactNameLength: 100,
  maxCompanyNameLength: 140,
  maxDestinationLength: 80,
  maxEmailLength: 254,
  maxWhatsappLength: 24,
})

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function normalizeText(value: string, maxLength: number) {
  return value.replace(/\s+/g, ' ').trim().slice(0, maxLength)
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase().slice(0, leadRequestPolicy.maxEmailLength)
}

function normalizeWhatsapp(value: string) {
  const compact = value.trim().replace(/[^\d+]/g, '')
  const leadingPlus = compact.startsWith('+') ? '+' : ''
  const digits = compact.replace(/\D/g, '').slice(0, 15)
  return `${leadingPlus}${digits}`.slice(0, leadRequestPolicy.maxWhatsappLength)
}

export function validateLeadRequest(draft: LeadRequestDraft): LeadRequestValidation {
  if (draft.website.trim().length > 0) {
    return { isValid: false, message: 'No fue posible enviar la solicitud.', request: null }
  }

  if (!draft.product || !allowedProducts.has(draft.product)) {
    return { isValid: false, message: 'Selecciona un producto.', request: null }
  }

  if (!draft.quantityRange || !allowedQuantityRanges.has(draft.quantityRange)) {
    return { isValid: false, message: 'Selecciona un rango aproximado de volumen.', request: null }
  }

  if (!draft.frequency || !allowedFrequencies.has(draft.frequency)) {
    return { isValid: false, message: 'Selecciona cómo necesitas comprar.', request: null }
  }

  const destination = normalizeText(draft.destination, leadRequestPolicy.maxDestinationLength)
  if (destination.length < 2) {
    return { isValid: false, message: 'Indica ciudad, estado o código postal aproximado.', request: null }
  }

  if (!draft.buyerType || !allowedBuyerTypes.has(draft.buyerType)) {
    return { isValid: false, message: 'Selecciona si compras como empresa o de forma personal.', request: null }
  }

  const contactName = normalizeText(draft.contactName, leadRequestPolicy.maxContactNameLength)
  if (contactName.length < 2) {
    return { isValid: false, message: 'Escribe el nombre de la persona de contacto.', request: null }
  }

  const normalizedCompanyName = normalizeText(draft.companyName, leadRequestPolicy.maxCompanyNameLength)
  if (draft.buyerType === 'business' && normalizedCompanyName.length < 2) {
    return { isValid: false, message: 'Escribe el nombre de la empresa o razón social.', request: null }
  }

  if (!draft.contactMethod || !allowedContactMethods.has(draft.contactMethod)) {
    return { isValid: false, message: 'Selecciona cómo prefieres que te contactemos.', request: null }
  }

  const email = normalizeEmail(draft.email)
  const whatsapp = normalizeWhatsapp(draft.whatsapp)

  if (draft.contactMethod === 'email' && !emailPattern.test(email)) {
    return { isValid: false, message: 'Escribe un correo electrónico válido.', request: null }
  }

  const whatsappDigits = whatsapp.replace(/\D/g, '')
  if (draft.contactMethod === 'whatsapp' && (whatsappDigits.length < 10 || whatsappDigits.length > 15)) {
    return { isValid: false, message: 'Escribe un número de WhatsApp válido con lada.', request: null }
  }

  const request: LeadRequest = {
    buyerType: draft.buyerType,
    contactName,
    companyName: draft.buyerType === 'business' ? normalizedCompanyName : null,
    product: draft.product,
    quantityRange: draft.quantityRange,
    destination,
    frequency: draft.frequency,
    contactMethod: draft.contactMethod,
    email: draft.contactMethod === 'email' ? email : null,
    whatsapp: draft.contactMethod === 'whatsapp' ? whatsapp : null,
  }

  return { isValid: true, message: null, request }
}
